import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const InventoryMovements = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'purchase',
    quantity: '',
    unit_cost: '',
    notes: ''
  });

  const { data: movementsData, isLoading } = useQuery(
    ['inventory-movements', productId],
    () => api.get(`/products/${productId}/inventory-movements`).then(res => res.data.data)
  );

  const addAdjustmentMutation = useMutation(
    (data) => api.post(`/products/${productId}/inventory-adjustment`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory-movements', productId]);
        toast.success('تم تسجيل الحركة بنجاح');
        setShowAddForm(false);
        setFormData({ type: 'purchase', quantity: '', unit_cost: '', notes: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'حدث خطأ');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    addAdjustmentMutation.mutate(formData);
  };

  const getTypeLabel = (type) => {
    const types = {
      sale: 'بيع',
      purchase: 'شراء',
      adjustment: 'تعديل يدوي',
      return: 'مرتجع'
    };
    return types[type] || type;
  };

  const getTypeBadge = (type) => {
    const styles = {
      sale: 'bg-red-100 text-red-800',
      purchase: 'bg-green-100 text-green-800',
      adjustment: 'bg-blue-100 text-blue-800',
      return: 'bg-yellow-100 text-yellow-800'
    };
    return styles[type] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/vendor/products')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">سجل حركة المخزون</h1>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            إضافة حركة
          </button>
        </div>

        {/* Add Movement Form */}
        {showAddForm && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">إضافة حركة مخزون</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    نوع الحركة
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="purchase">شراء</option>
                    <option value="adjustment">تعديل يدوي</option>
                    <option value="return">مرتجع</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الكمية (موجب للإضافة، سالب للخصم)
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    التكلفة للوحدة (اختياري)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unit_cost}
                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ملاحظات (اختياري)
                  </label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={addAdjustmentMutation.isLoading}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {addAdjustmentMutation.isLoading ? 'جاري الحفظ...' : 'حفظ'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Movements List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">التاريخ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">النوع</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الكمية</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">قبل</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">بعد</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعر/التكلفة</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">رقم الطلب</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">ملاحظات</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">بواسطة</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {movementsData?.data?.map((movement) => (
                  <tr key={movement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(movement.created_at).toLocaleString('ar-SA')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadge(movement.type)}`}>
                        {getTypeLabel(movement.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`flex items-center gap-1 font-medium ${movement.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {movement.quantity > 0 ? <PlusIcon className="w-4 h-4" /> : <MinusIcon className="w-4 h-4" />}
                        {Math.abs(movement.quantity)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {movement.quantity_before}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {movement.quantity_after}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {movement.unit_price ? `${movement.unit_price} جنيه` : movement.unit_cost ? `${movement.unit_cost} جنيه` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {movement.order?.order_number || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {movement.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {movement.created_by?.name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {movementsData?.data?.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              لا توجد حركات مخزون لهذا المنتج
            </div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
};

export default InventoryMovements;
